import { ENV } from '@configs/env';
import { extractRolesFromUser } from '@ability/constants';
import { normalizePermissions, setPermissionsInLocalStorage } from '@utils/authPermissions';

const REFRESH_TIMEOUT_MS = 12000;

function getRefreshUrl() {
    const base = String(ENV.API_URL || '').replace(/\/$/, '');
    return `${base}/auth/refresh`;
}

/**
 * Refresh via fetch (not shared axios) with Connection: close to avoid wedged keep-alive slots.
 */
async function postRefreshRequest(refreshTokenValue) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

    try {
        const response = await fetch(getRefreshUrl(), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Connection: 'close',
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
            },
            body: JSON.stringify({ refreshToken: refreshTokenValue }),
            signal: controller.signal,
            cache: 'no-store',
        });

        if (!response.ok) {
            const err = new Error(`Refresh HTTP ${response.status}`);
            err.status = response.status;
            try {
                err.body = await response.json();
            } catch {
                // ignore
            }
            throw err;
        }

        return response.json();
    } finally {
        clearTimeout(timeoutId);
    }
}

export const refreshToken = async (refreshTokenValue, setAuthState, logout, opts = {}) => {
    try {
        if (!refreshTokenValue) throw new Error('Missing refresh token');

        const json = await postRefreshRequest(refreshTokenValue);
        const responseData = json.data || json;
        const { user: rawUser, accessToken, refreshToken: newRefresh } = responseData;

        if (!rawUser) {
            setAuthState({
                user: null,
                role: null,
                roles: [],
                permissions: [],
                isAuthenticated: true,
                accessToken: accessToken,
                refreshToken: newRefresh,
            });
            setPermissionsInLocalStorage([]);
            return { accessToken, refreshToken: newRefresh };
        }

        const permissions = normalizePermissions(rawUser.permissions);
        const role = rawUser.role ?? (Array.isArray(rawUser.roles) && rawUser.roles.length ? rawUser.roles[0] : null);
        const user = { ...rawUser, permissions, role };
        const { roles } = extractRolesFromUser(user);

        setAuthState({
            user,
            role,
            roles: roles ?? [],
            permissions,
            isAuthenticated: true,
            accessToken: accessToken,
            refreshToken: newRefresh,
        });
        setPermissionsInLocalStorage(permissions);

        return {
            accessToken: accessToken,
            refreshToken: newRefresh,
        };
    } catch (error) {
        const isAbort =
            error?.name === 'AbortError' ||
            error?.name === 'CanceledError' ||
            String(error?.message || '').toLowerCase().includes('aborted');

        if (isAbort) {
            console.warn('Refresh token request was aborted:', error?.message);
            throw error;
        }

        const message =
            error?.body?.message ||
            error?.body?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Refresh failed';
        console.error('Refresh token failed:', message, {
            code: error?.code,
            status: error?.status ?? error?.response?.status,
        });

        const status = error?.status ?? error?.response?.status;
        const isAuthError = status === 400 || status === 401 || status === 403;

        if (isAuthError) {
            if (!opts.skipLogoutOnError) {
                logout?.();
            }
            return null;
        }

        throw error;
    }
};

let refreshTokensSingletonPromise = null;

export const refreshTokensSingleton = (getRefreshToken, setAuthState, logout, opts = {}) => {
    if (refreshTokensSingletonPromise) {
        return refreshTokensSingletonPromise;
    }
    refreshTokensSingletonPromise = (async () => {
        try {
            const rt = typeof getRefreshToken === 'function' ? getRefreshToken() : null;
            if (!rt) return null;
            return await refreshToken(rt, setAuthState, logout, opts);
        } finally {
            refreshTokensSingletonPromise = null;
        }
    })();
    return refreshTokensSingletonPromise;
};
